import { Box, Tab, Tabs, styled } from '@mui/material';
import React from 'react';
import theme from '../../../theme';
import {
  BracketsAngleIcon,
  FileTextIcon,
  PhoneTransferIcon,
  UploadIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import CallForwardingRules from './TransferCallTabSection/CallForwardingRules';
import GenerateExtractor from './ExtractTabSection/GenerateExtractor';

import WebHook from '../FunctionTooling/WebHook';
import WebForm from '../FunctionTooling/WebForm';
import UltravoxTools from './ToolCreationTabSection/UltravoxTools';
const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    minHeight: '72px',
    padding: '16px',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: 'none',
    width: '250px', // Keep current width
    maxWidth: '250px', // Ensure max width is also fixed
    transition: 'none', // Remove transition effects
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      backgroundColor: '#E7F0FF',
      borderRadius: '0px',
    },
    '&:focus': {
      outline: 'none', // Remove focus outline
    },
    '&:active': {
      transform: 'none', // Remove any transform on click
    },
  },
});

const FunctionTabs = ({
  onTabChange,
}: {
  onTabChange?: (tabIndex: number) => void;
}) => {
  const [selectedTab, setSelectedTab] = React.useState(0);

  const tabItems = [
    {
      label: 'Extract',
      desc: 'Extract and structure data automatically using AI',
      icon: <UploadIcon size={16} />,
      component: <GenerateExtractor />,
    },
    {
      label: 'Webhook',
      desc: 'Send real-time notifications to external systems',
      icon: <BracketsAngleIcon size={16} />,
      component: <WebHook />,
    },
    {
      label: 'Webform',
      desc: 'Create and manage webforms to collect information from users',
      icon: <FileTextIcon size={16} />,
      component: <WebForm />,
    },
    {
      label: 'Transfer Call',
      desc: 'Transfer conversations to other agents or departments',
      icon: <PhoneTransferIcon size={16} />,
      component: <CallForwardingRules />,
    },
    {
      label: 'Tool Creation',
      desc: 'Create and manage tools to use in your conversations',
      icon: <WrenchIcon size={16} />,
      component: <UltravoxTools />,
    },
    // {
    //   label: 'Email',
    //   desc: 'Send personalized emails based on conversations',
    //   icon: <EnvelopeSimple size={16} />,
    //   component: <AddEmailTemplate />,
    // },
    // {
    //   label: 'Calendar',
    //   desc: 'Schedule appointments and meetings from conversations',
    //   icon: <CalendarDots size={16} />,
    //   component: <UnderDevelopment />,
    // },
    // {
    //   label: 'API',
    //   desc: 'Integrate with external services via API endpoints',
    //   icon: <BracketsCurly size={16} />,
    //   component: <UnderDevelopment />,
    // },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
      }}
    >
      <Box
        sx={{
          flexShrink: 0, // Prevent box from shrinking
          height: '100%',
          overflow: 'hidden', // Hide overflow
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${theme.palette.divider}`, // Add border to container
        }}
      >
        <Box
          sx={{
            overflowY: 'auto', // Make only this box scrollable
            height: '100%',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            width: '250px', // Match tab width
          }}
        >
          <StyledTabs
            value={selectedTab}
            onChange={handleTabChange}
            orientation="vertical"
            sx={{
              minWidth: '100%',
            }}
            slotProps={{
              indicator: {
                style: { transition: 'none' },
              },
            }}
          >
            {tabItems.map((tab, i) => (
              <Tab
                key={i}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor:
                            selectedTab === i ? '#FFFFFF' : '#F8F9FC',
                          p: '4px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'none',
                        }}
                      >
                        {tab.icon}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          transition: 'none',
                        }}
                      >
                        {tab.label}
                      </Box>
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '12px',
                        color: 'text.secondary',
                        textAlign: 'left',
                        width: '100%',
                        whiteSpace: 'normal',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        transition: 'none',
                      }}
                    >
                      {tab.desc}
                    </Box>
                  </Box>
                }
              />
            ))}
          </StyledTabs>
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'hidden', // Hide overflow
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            overflowY: 'auto', // Make only this box scrollable
            height: '100%',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          <Box sx={{ p: 2, height: '100%' }}>
            <Box sx={{ height: '100%' }}>{tabItems[selectedTab].component}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FunctionTabs;
